import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import clsx from 'clsx';
import ChevronDown from '@/shared/icons/ChevronDown';
import SelectOption from '@/shared/components/UI/Select/SelectOption';
import { useAudioPlayback } from '@/hooks/useAudioPlayback';

/**
 * Select - A refactored select component using v3 design system
 * 
 * Features:
 * - Clean separation of concerns (audio, options, styling)
 * - Proper controlled component pattern
 * - v3 design system classes
 * - Accessible via Headless UI
 * - Support for custom option rendering, audio playback, and delete actions
 * - Support for custom date range picker
 * - Consistent sizing and styling across the app
 */
const Select = ({
  // Core props
  options = [],
  value,
  onChange,
  placeholder = 'Select an item',
  disabled = false,
  
  // Display props
  label,
  selectedChild, // Custom display for selected value
  
  // Validation props
  error,
  helperText,
  canHaveError = true,
  
  // Size and variant props
  size = 'md', // 'sm' (32px), 'md' (40px), 'lg' (48px)
  variant = 'default', // 'default' (with border/bg), 'transparent' (no border/bg)
  
  // Styling props (minimal - use size/variant instead)
  className = '',
  wrapperClassName = '',
  // Backward compatibility props
  listButtonClass = '',
  listButtonClassName = '',
  chevronIconClass = '',
  chevronIconClassName = '',
  chevronClassName = '', // Alias for chevronIconClassName
  optionsClassName = '',
  optionContainerClassName = '',
  selectClassName = '',
  
  // Layout props
  dropdownWidth = 'auto',
  
  // Custom rendering
  children, // Custom option rendering
  customContent, // Custom content to render instead of options (e.g., date picker)
  customTriggerValue, // Value that triggers custom content (e.g., 'custom')
  onCustomContentClose, // Callback when custom content closes
  
  // Sorting props
  sortSelectedToTop = true, // When true, selected option appears at the top of the dropdown
  
  // Other props
  onOptionClick,
  ...props
}) => {
  const listButtonRef = useRef(null);
  const popoverButtonRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState('auto');
  const [showCustomContent, setShowCustomContent] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { playingId, playSound } = useAudioPlayback();

  // Calculate error state early
  const hasError = !!error;
  const hasHelperText = !!helperText && !hasError;

  // Check if current value is the custom trigger value
  const isCustomMode = customContent && value === customTriggerValue;

  // Size mapping for button heights
  const sizeClasses = {
    sm: 'h-8', // 32px
    md: 'h-10', // 40px
    lg: 'h-12', // 48px
  };

  // Size mapping for text
  const textSizeClasses = {
    sm: 'text-v3-h11', // 12px
    md: 'text-v3-h8', // 15px
    lg: 'text-v3-h7', // 16px
  };

  // Variant classes
  const variantClasses = {
    default: clsx(
      'border',
      hasError ? 'border-v3-red-dark' : 'border-v3-black-inactive',
      'bg-white'
    ),
    transparent: clsx(
      'bg-transparent',
      'border-0'
    ),
  };

  // Merge backward compatibility props
  const mergedListButtonClassName = listButtonClassName || listButtonClass;
  const mergedChevronClassName = chevronIconClassName || chevronIconClass || chevronClassName;

  // Calculate dropdown width
  useEffect(() => {
    if (dropdownWidth !== 'auto') {
      const width = typeof dropdownWidth === 'number' 
        ? `${dropdownWidth}px` 
        : dropdownWidth;
      setButtonWidth(width);
    } else if (listButtonRef.current) {
      setButtonWidth(`${listButtonRef.current.offsetWidth}px`);
    }
  }, [dropdownWidth]);

  const safeOptions = Array.isArray(options) ? options : [];

  // Sort options to place selected option at the top
  const sortedOptions = useMemo(() => {
    if (!sortSelectedToTop || !value || safeOptions.length === 0) {
      return safeOptions;
    }

    const optionsCopy = [...safeOptions];
    const selectedIndex = optionsCopy.findIndex((option) => {
      const optionValue = option.value !== undefined ? option.value : option;
      return optionValue === value;
    });

    if (selectedIndex > 0) {
      const selectedOption = optionsCopy[selectedIndex];
      optionsCopy.splice(selectedIndex, 1);
      optionsCopy.unshift(selectedOption);
    }

    return optionsCopy;
  }, [safeOptions, value, sortSelectedToTop]);

  // Handle option change (only fires when value changes, mainly via keyboard now for custom)
  const handleChange = (newValue) => {
    onChange(newValue);
    
    if (customContent && newValue === customTriggerValue) {
      // For keyboard selection, we need a slight delay to let the Listbox close
      setTimeout(() => {
        if (popoverButtonRef.current) {
          popoverButtonRef.current.click();
        }
      }, 150);
    }
  };

  // Handle individual option click (fires even if value doesn't change)
  const handleOptionClick = (optionValue) => {
    if (onOptionClick) {
      onOptionClick(optionValue);
    }

    // Special logic for custom trigger (Mouse clicks)
    if (customContent && optionValue === customTriggerValue) {
      // Trigger manually for all custom mouse clicks. 
      // We don't set shouldAutoOpenCustom here to avoid triggering the useEffect.
      setIsPopoverOpen(true);
      
      setTimeout(() => {
        if (popoverButtonRef.current) {
          popoverButtonRef.current.click();
        }
      }, 0);
    }
  };

  // Handle custom content close
  const handleCustomContentClose = () => {
    setIsPopoverOpen(false);
    if (onCustomContentClose) {
      onCustomContentClose();
    }
  };

  // Find selected option label
  const selectedOption = safeOptions.find((option) => {
    const optionValue = option.value || option;
    return optionValue === value;
  });
  
  const displayValue = selectedChild || selectedOption?.label || placeholder;

  return (
    <div
      className={clsx(
        'flex',
        'flex-col',
        'items-start',
        'w-full',
        wrapperClassName
      )}
    >
      {/* Label and Select Wrapper */}
      <div
        className={clsx(
          'flex',
          'flex-col',
          'items-start',
          'gap-v3-xs',
          'self-stretch',
          'w-full'
        )}
      >
        {/* Label */}
        {label && (
          <label
            className={clsx(
              'text-v3-black-max',
              'text-v3-h10',
              'font-[510]',
              'not-italic'
            )}
          >
            {label}
          </label>
        )}

        <div className="relative w-full">
          <Listbox
            value={value}
            onChange={handleChange}
            disabled={disabled}
            as="div"
            className={clsx(
              'relative',
              'group',
              'w-full',
              className
            )}
          >
          <ListboxButton
            ref={listButtonRef}
            className={clsx(
              'relative',
              'w-full',
              'flex',
              sizeClasses[size],
              'py-v3-xs',
              'pl-3',
              'pr-3',
              'items-center',
              'self-stretch',
              'rounded-v3-xs',
              variantClasses[variant],
              'text-left',
              textSizeClasses[size],
              'text-v3-black-max',
              'font-normal',
              disabled && 'cursor-not-allowed',
              !disabled && 'cursor-pointer',
              mergedListButtonClassName
            )}
            data-testid={`select-${props.name || props.id || ''}`}
            {...props}
          >
            <div className="flex items-center gap-v3-xs flex-1 min-w-0 pr-2">
              {selectedOption?.imageUrl && (
                <img
                  src={selectedOption.imageUrl}
                  alt={displayValue}
                  className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                />
              )}
              <span
                className={clsx(
                  'truncate',
                  'min-w-0',
                  disabled ? 'text-v3-black-inactive' : 'text-v3-black-max'
                )}
              >
                {displayValue}
              </span>
            </div>
            
            <div className="flex items-center flex-shrink-0 pointer-events-none">
              <ChevronDown 
                strokeColor={
                  disabled 
                    ? 'var(--v3-black-inactive)' 
                    : 'var(--v3-purple-primary)'
                }
                className={clsx(
                  'transition-all',
                  'duration-200',
                  'group-data-[open]:rotate-180',
                  'text-v3-purple-primary',
                  'group-hover:text-v3-purple-medium',
                  mergedChevronClassName
                )} 
              />
            </div>
          </ListboxButton>

          <ListboxOptions
            anchor="bottom"
            portal
            className={clsx(
              'border',
              'border-v3-card-border',
              'rounded-v3-ms',
              'z-[9999]',
              'bg-v3-dropdown',
              '!max-h-[400px]',
              'overflow-y-auto',
              'shadow-v3-card',
              'no-scrollbar',
              optionsClassName
            )}
            style={{ 
              zIndex: 9999, 
              '--button-width': buttonWidth,
              width: dropdownWidth !== 'auto' 
                ? (typeof dropdownWidth === 'number' 
                    ? `${dropdownWidth}px` 
                    : dropdownWidth)
                : 'var(--button-width)'
            }}
          >
            {sortedOptions.length > 0 ? (
              sortedOptions.map((option, idx) => (
                <SelectOption
                  key={idx}
                  option={option}
                  index={idx}
                  selectedValue={value}
                  playingId={playingId}
                  onPlaySound={playSound}
                  onOptionClick={handleOptionClick}
                  customTriggerValue={customTriggerValue}
                  optionContainerClassName={optionContainerClassName}
                  selectClassName={selectClassName}
                />
              ))
            ) : (
              children
            )}
          </ListboxOptions>
          </Listbox>

          {/* Custom Content Popover */}
          {customContent && (
            <Popover className="absolute inset-0 pointer-events-none">
              <PopoverButton
                ref={popoverButtonRef}
                className="w-full h-full opacity-0 pointer-events-none"
              />
              <PopoverPanel
                anchor="bottom"
                className="pointer-events-auto z-[10000]"
                style={{ zIndex: 10000 }}
              >
                {({ close }) => (
                  React.cloneElement(customContent, {
                    onClose: () => {
                      close();
                      handleCustomContentClose();
                    },
                    onCancel: () => {
                      close();
                      if (customContent.props.onCancel) {
                        customContent.props.onCancel();
                      }
                      handleCustomContentClose();
                    },
                    onApply: (...args) => {
                      close();
                      if (customContent.props.onApply) {
                        customContent.props.onApply(...args);
                      }
                      handleCustomContentClose();
                    },
                  })
                )}
              </PopoverPanel>
            </Popover>
          )}
        </div>
      </div>

      {/* Error Text / Helper Text */}
      {canHaveError && (
        <div
          className={clsx(
            'flex',
            'pt-v3-xxs',
            'pl-3',
            'items-start',
            'gap-[10px]',
            'min-h-[20px]'
          )}
        >
          {hasError ? (
            <span
              className={clsx(
                'text-v3-red-dark',
                'text-v3-h11',
                'font-normal',
                'not-italic'
              )}
            >
              {error}
            </span>
          ) : (
            hasHelperText && (
              <span
                className={clsx(
                  'text-v3-black-secondary',
                  'text-v3-h11',
                  'font-normal',
                  'italic'
                )}
              >
                {helperText}
              </span>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
